import * as Sentry from '@sentry/react';

const SENTRY_DSN =
  'https://b71ba64d5d7dd1b33c33f09e74656419@o99218.ingest.us.sentry.io/4511473395105792';

const SENSITIVE_FIELDS = [
  'email',
  'fullName',
  'userName',
  'accountName',
  'churchName',
  'password',
  'token',
  'authorization',
  'cookie',
  'sessionId',
];

const SCRUBBED = '[Filtered]';

/**
 * Recursively scrub sensitive fields from a plain object.
 * Returns a new object — the original is not mutated.
 */
const scrubObject = (obj) => {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(scrubObject);

  return Object.keys(obj).reduce((acc, key) => {
    const lowerKey = key.toLowerCase();
    if (
      SENSITIVE_FIELDS.some((field) => lowerKey.includes(field.toLowerCase()))
    ) {
      acc[key] = SCRUBBED;
    } else {
      acc[key] = scrubObject(obj[key]);
    }
    return acc;
  }, {});
};

/**
 * Strip email-like patterns from strings to prevent PII leakage
 * in error messages or exception values.
 */
const scrubEmailsFromString = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    '[email]'
  );
};

/**
 * Scrub PII from a Sentry event before it is sent.
 */
const beforeSend = (event) => {
  // Never send user PII
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }

  // Scrub request headers (e.g. Authorization)
  if (event.request && event.request.headers) {
    event.request.headers = scrubObject(event.request.headers);
  }

  // Scrub request data/body
  if (event.request && event.request.data) {
    if (typeof event.request.data === 'object') {
      event.request.data = scrubObject(event.request.data);
    } else if (typeof event.request.data === 'string') {
      event.request.data = scrubEmailsFromString(event.request.data);
    }
  }

  // Scrub exception values for email patterns
  if (event.exception && event.exception.values) {
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: scrubEmailsFromString(ex.value),
    }));
  }

  // Scrub extra context
  if (event.extra) {
    event.extra = scrubObject(event.extra);
  }

  // Scrub contexts
  if (event.contexts) {
    event.contexts = scrubObject(event.contexts);
  }

  return event;
};

/**
 * Scrub PII from breadcrumbs (e.g. XHR breadcrumbs that may
 * contain auth tokens or user data in URLs/headers).
 */
const beforeBreadcrumb = (breadcrumb) => {
  // Drop console.warn breadcrumbs and any message starting with "Warning:"
  if (
    (breadcrumb.category === 'console' && breadcrumb.level === 'warning') ||
    /^warning:/i.test(breadcrumb.message)
  ) {
    return null;
  }

  if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
    if (breadcrumb.data) {
      breadcrumb.data = scrubObject(breadcrumb.data);
    }
  }

  if (breadcrumb.message) {
    breadcrumb.message = scrubEmailsFromString(breadcrumb.message);
  }

  return breadcrumb;
};

function deriveEnvironment() {
  const { hostname } = window.location;
  if (hostname.includes('local')) {
    return 'local';
  } else if (hostname.includes('dev') || hostname.includes('int')) {
    return 'integration';
  } else if (hostname.includes('stage') || hostname.includes('uat')) {
    return 'user-acceptance';
  } else {
    return 'production';
  }
}

const initSentry = () => {
  if (!SENTRY_DSN) {
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: deriveEnvironment(),

    // Capture 100% of error events — no sampling for errors
    sampleRate: 1.0,

    // Do not send PII by default (cookies, user IP, etc.)
    sendDefaultPii: false,

    beforeSend,
    beforeBreadcrumb,

    // Limit breadcrumbs to keep payload size reasonable
    maxBreadcrumbs: 50,

    integrations: [
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.replayIntegration(),
    ],

    // Ignore common non-actionable errors to reduce noise in Sentry
    ignoreErrors: [
      // OIDC Client Library Errors
      'No matching state found in storage',
      'Worker was terminated',
      'Load failed',
      // Authorization errors are not sent because they are used to determine if the user is logged in
      'Request failed with status code 403',
      // Fullstory API error
      'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
      // Test error
      'Error check',
      'Warning: ',
      'warning: ',
    ],
  });
};

export {
  initSentry,
  beforeSend,
  beforeBreadcrumb,
  scrubObject,
  scrubEmailsFromString,
};
export default initSentry;
